import React from 'react';
import { Box, ButtonBase, Collapse, Divider, Paper, Typography, useTheme } from '@mui/material';
import {
  CheckCircleOutlineOutlined as CheckCircleOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { CaseStatusData } from '../types/CaseStatusData';
import { CARD_PADDING } from '../caseStatusTheme';
import { useCaseStatusLanguage } from '../../../../routes/CaseStatus/CaseStatusLanguageProvider';

type Props = {
  data: CaseStatusData;
  detailsExpanded: boolean;
  onToggleDetails: () => void;
  children?: React.ReactNode;
};

const PaymentReceivedCard = ({ data, detailsExpanded, onToggleDetails, children }: Props) => {
  const theme = useTheme();
  const { t, fmtCurrency } = useCaseStatusLanguage();

  return (
    <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }} data-testid="payment-received-card">
      <Box sx={{ p: CARD_PADDING }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <CheckCircleOutlineIcon sx={{ color: theme.palette.grey[600], fontSize: 28, flexShrink: 0, mt: '3px' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }} data-testid="payment-received-title">
              {t('PaymentReceived')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.grey[600] }}>
              {t('PaymentReceivedBody')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.grey[100],
          px: CARD_PADDING,
          py: 1.5,
        }}
      >
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('OriginalClaim')}
          </Typography>
          <Typography
            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
            data-testid="payment-received-original-claim"
          >
            {fmtCurrency(data.originalClaim, data.currency)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: CARD_PADDING, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {t('RemainingAmountDisclaimer')}
        </Typography>
      </Box>

      <Divider />

      <ButtonBase
        onClick={onToggleDetails}
        data-testid="payment-received-toggle-details"
        aria-expanded={detailsExpanded}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          px: CARD_PADDING,
          py: 1.5,
        }}
      >
        <Typography sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
          {detailsExpanded ? t('HidePaymentDetails') : t('ShowPaymentDetails')}
        </Typography>
        {detailsExpanded ? (
          <ExpandLessIcon sx={{ color: theme.palette.secondary.main }} />
        ) : (
          <ExpandMoreIcon sx={{ color: theme.palette.secondary.main }} />
        )}
      </ButtonBase>

      <Collapse in={detailsExpanded} unmountOnExit>
        <Divider />
        <Box sx={{ p: CARD_PADDING }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

export default PaymentReceivedCard;
